"use client";

import React, { useState } from "react";
import { BookOpen, CircleCheck as CheckCircle2, ExternalLink, Sparkles, TrendingUp, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const takeaways = [{
  title: "Understand Biblical Zionism",
  description: "Explore the scriptural foundations connecting Israel's covenant significance to the modern church."
}, {
  title: "Navigate Complex Conversations",
  description: "Gain grace-filled discernment for one of today's most polarized and misunderstood topics."
}, {
  title: "Bridge Faith and Current Events",
  description: "Connect ancient prophecy with modern geopolitical history through careful biblical exegesis."
}];
export default function FeaturedBookHero() {
  const [isExpanded, setIsExpanded] = useState(false);
  const featureTags = [{
    label: "Faith-Centered"
  }, {
    label: "Thought-Provoking"
  }];
  return <section id="featuredbookhero" data-nav="light" className="relative overflow-hidden bg-background py-10 md:py-14 lg:py-16">
      {/* Ambient background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -z-10 hidden h-96 w-full -translate-x-1/2 max-w-7xl rounded-full bg-accent/15 blur-3xl md:block" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-10 -z-10 hidden h-72 w-72 rounded-full bg-card/60 blur-2xl md:block" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
          
          {/* Book Cover Column (Left on Desktop, First on Mobile) */}
          <div className="flex justify-center lg:col-span-5">
            <div className="group relative w-full max-w-xs sm:max-w-sm lg:max-w-md">
              {/* Backing glow behind book */}
              <div aria-hidden="true" className="absolute -inset-2 rounded-2xl bg-accent/20 blur-xl transition-[opacity] duration-500 group-hover:opacity-100" />
              
              {/* Book container */}
              <div className="relative overflow-hidden rounded-xl border border-border bg-card p-3 shadow-3xl sm:p-4">
                <AspectRatio ratio={2 / 3} className="overflow-hidden rounded-lg bg-muted/40">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/AroundTheCoffeeTable_updated.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/AroundTheCoffeeTable_updated.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/AroundTheCoffeeTable_updated.jpg-wvc-sizes" alt="Around the Coffee Table: Israel, Zionism, & The Church Book Cover" loading="lazy" decoding="async" className="h-full w-full object-contain object-center shadow-inner transition-transform duration-500 ease-out group-hover:scale-[1.02]" />
                </AspectRatio>

                {/* Subtle sheen highlight banner */}
                <div className="mt-3 flex items-center justify-between px-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <BookOpen className="size-3.5 text-accent" />
                    Paperback &amp; Kindle
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-md bg-accent/20 text-accent-foreground">
                      <BookOpen className="size-4 text-accent" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">Dustin Royer</p>
                      <p className="text-[11px] text-muted-foreground">Author &amp; Bible Teacher</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Book Details & CTA Column (Right on Desktop, Second on Mobile) */}
          <div className="flex flex-col items-start lg:col-span-7">
            {/* Announcement Badge */}
            <Badge variant="outline" className="mb-6 gap-2 rounded-full border-accent/40 bg-accent/10 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-foreground uppercase">
              <TrendingUp className="size-3.5 text-accent" />
              <span>NEW BOOK AVAILABLE NOW</span>
            </Badge>

            {/* Editorial Serif Heading */}
            <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-tight">
              Around the Coffee Table:{" "}
              <span className="bg-gradient-to-r from-accent to-accent-foreground bg-clip-text text-accent">
                Israel, Zionism, &amp; The Church
              </span>
            </h1>

            {/* Author attribution */}
            <p className="mt-2 text-base font-medium tracking-wide text-muted-foreground sm:text-lg">
              By <span className="text-foreground">Dustin Royer</span>
            </p>

            {/* Subheadline / Secondary Text */}
            <p className="mt-6 text-lg text-secondary-foreground sm:text-xl">
              Biblical Truth in an Age of Politics, Ideology, and Confusion
            </p>

            <div className={`mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground transition-[max-height] duration-300 ${isExpanded ? "max-h-none" : "max-h-24 sm:max-h-28 overflow-hidden"}`}>
              <p>
                A balanced, scripturally grounded exploration designed to bring clarity, biblical
                discernment, and grace-filled conversation to one of today&apos;s most debated and misunderstood topics.
              </p>
              <p className="mt-3">
                In this comprehensive work, Dustin Royer examines the complex intersection of covenant theology,
                biblical prophecy, and modern geopolitical history. Moving beyond polarized rhetoric and sensationalism,
                each chapter walks through key scriptural passages with careful exegesis and pastoral sensitivity.
              </p>
              <p className="mt-3">
                Whether you are wrestling with theological questions, leading a Bible study, or seeking thoughtful
                answers for conversations with friends and family, this book provides the biblical framework and historical
                context needed to navigate one of the most vital dialogues in the contemporary Church.
              </p>
            </div>

            <Button type="button" variant="link" onClick={() => setIsExpanded(prev => !prev)} className="h-auto p-0 text-sm font-medium text-accent hover:underline hover:text-accent/90">
              {isExpanded ? "Read less" : "Read more"}
            </Button>

            {/* Feature Indicator Tags */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {featureTags.map((tag, index) => <div key={tag.label} data-index={index} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1 text-xs font-medium text-foreground shadow-2xs">
                  <span className="size-2 rounded-full bg-accent" />
                  <span>{tag.label}</span>
                </div>)}
              <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground pl-1">
                <Sparkles className="size-3.5 text-accent" />
                <span>Available for study groups</span>
              </div>
            </div>

            {/* What You Will Discover Inside Takeaways */}
            <Separator className="my-6 max-w-md bg-border/80" />
            <div className="space-y-3 max-w-2xl">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                What You Will Discover Inside
              </h2>
              <div className="space-y-3">
                {takeaways.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent">
                      <CheckCircle2 className="size-3.5" />
                    </div>
                    <div>
                      <span className="font-semibold text-foreground text-sm">{item.title}: </span>
                      <span className="text-sm text-muted-foreground">{item.description}</span>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Primary Action Button */}
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="group relative shadow-md transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://a.co/d/08RN0Lqc" newTab className="inline-flex items-center gap-2">
                  <span>Buy on Amazon</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="group">
                <Link to="https://www.youtube.com/playlist?list=PLEpm7C4gv0Hc" newTab className="flex items-center gap-2 font-medium">
                  <Youtube className="size-4" />
                  <span>View Study Resources</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}